import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useLazyGetServiceBulletinReportQuery } from '../../../../api/endpoints/serviceBulletinReport';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { matchModeToOperator } from '../../../../utils/filterUtils';

const INITIAL_FILTER_STATE = {
    qmnum: null,
    qmnumMatchMode: null,
    bulletinName: null,
    bulletinNameMatchMode: null,
    bulletinDescription: null,
    bulletinDescriptionMatchMode: null,
    isApplied: null,
};

// Backend column mapping
const backendColumnMap = {
    qmnum: 'Qmnum',
    bulletinName: 'BulletinName',
    bulletinDescription: 'BulletinDescription', 
};

const getBackendColumnName = (field) => {
    if (!field) return null;
    return backendColumnMap[field] || null;
};

const getOperatorValue = (matchMode) => {
    return matchModeToOperator[matchMode] || matchModeToOperator[FilterMatchMode.CONTAINS];
};

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    const pushFilter = (field, value, matchMode) => {
        if (value === null || value === undefined || value === '') return;
        const column = getBackendColumnName(field);
        if (!column) return;

        let filterValue = String(value);
        if (filterValue && typeof filterValue === 'string') {
            filterValue = filterValue.trim();
        }
        if (filterValue === '') return;

        filters.push({
            column,
            operator: getOperatorValue(matchMode),
            value: filterValue
        });
    };

    pushFilter('qmnum', filtersState?.qmnum, filtersState?.qmnumMatchMode);
    pushFilter('bulletinName', filtersState?.bulletinName, filtersState?.bulletinNameMatchMode);
    pushFilter('bulletinDescription', filtersState?.bulletinDescription, filtersState?.bulletinDescriptionMatchMode);

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = getBackendColumnName(field);
    if (!column) return null;
    return [
        {
            column,
            direction: order === 1 ? 1 : 2
        }
    ];
};

export default function useServiceBulletinReport() {
    const initialLazyParams = { first: 0, rows: 10, page: 1 };
    const { th, getCurrentLanguageId } = useLanguage();

    const [lazyParams, setLazyParams] = useState(initialLazyParams);
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);
    const [chassisNo, setChassisNo] = useState(null);

    const [getServiceBulletinReport, { data, isLoading: isFetching, isError, error }] = useLazyGetServiceBulletinReportQuery();

    const queryParams = useMemo(() => {
        const filters = buildDynamicFilters(activeFilters);
        const sorts = buildDynamicSorts(sortField, sortOrder);

        return {
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            languageId: getCurrentLanguageId(),
            filters: filters && filters.length > 0 ? filters : undefined,
            sorts: sorts && sorts.length > 0 ? sorts : undefined,
            chassisNo: chassisNo || undefined,
            isApplied: chassisNo && activeFilters?.isApplied != null
            ? activeFilters.isApplied
            : undefined,
        };
    }, [lazyParams.page, lazyParams.rows, activeFilters, sortField, sortOrder, chassisNo]);

    useEffect(() => {
        getServiceBulletinReport(queryParams);
    }, [queryParams, getServiceBulletinReport]);

    const items = data ? (Array.isArray(data.items) ? data.items : []) : [];
    const totalCount = data ? data.totalCount ?? 0 : 0;

    const handleFilterChange = useCallback(
        (updatedFilters) => {
            setActiveFilters({
                qmnum: updatedFilters.qmnum || null,
                qmnumMatchMode: updatedFilters.qmnumMatchMode || null,
                bulletinName: updatedFilters.bulletinName || null,
                bulletinNameMatchMode: updatedFilters.bulletinNameMatchMode || null,
                bulletinDescription: updatedFilters.bulletinDescription || null,
                bulletinDescriptionMatchMode: updatedFilters.bulletinDescriptionMatchMode || null,
                 isApplied: updatedFilters.isApplied ?? null,
            });

            setLazyParams(initialLazyParams);
        },
        [initialLazyParams]
    );

    const handleAdvancedFilterApply = useCallback(
        ({ chassisNo: newChassisNo }) => {
            setChassisNo(newChassisNo || null);
            setLazyParams(initialLazyParams);
        },
        [initialLazyParams]
    );

    const resetFilters = useCallback(() => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setSortField(null);
        setSortOrder(0);
        setChassisNo(null);
        setLazyParams(initialLazyParams);
    }, [initialLazyParams]);

    const handleSortChange = useCallback(
        (event) => {
            const nextSortField = event?.sortField || null;
            const nextSortOrder = event?.sortOrder || 0;
            const backendColumn = getBackendColumnName(nextSortField);

            if (!backendColumn && nextSortOrder !== 0) {
                return;
            }

            setSortField(nextSortField);
            setSortOrder(nextSortOrder);
            setLazyParams(initialLazyParams);
        },
        [initialLazyParams]
    );

    const onPage = useCallback((e) => {
        setLazyParams({ first: e.first, rows: e.rows, page: e.page + 1 });
    }, []);

    return {
        items,
        totalCount,
        isFetching,
        isError,
        error,
        lazyParams,
        activeFilters,
        handleFilterChange,
        resetFilters,
        onPage,
        sortField,
        sortOrder,
        onSort: handleSortChange,
        handleAdvancedFilterApply,
        chassisNo   
    };
}
